import React, { useState, useMemo, useCallback, useEffect } from 'react'
import { List, Card, Typography, Tag, Menu, Layout, Tooltip } from 'antd'
import { CheckOutlined, CloseOutlined, InfoCircleOutlined } from '@ant-design/icons'
import styles from './GroupedSettings.css'

const { Sider, Content } = Layout
const { Title, Text } = Typography

// Reuse the DisplayValue function from the Table component
function DisplayValue(props) {
    let v;
    if (props.value != undefined)
        v = props.value;
    else v = "";

    // Helper function to decode HTML entities without external dependencies
    const decodeHtmlEntities = (content) => {
        if (typeof content !== 'string') return String(content);

        // Create a temporary element to use the browser's built-in HTML entity decoding
        const tempElement = document.createElement('div');
        tempElement.innerHTML = content;
        const decoded = tempElement.textContent || tempElement.innerText;

        return decoded;
    };

    if (props.processor == "FileUpload" && v != "") {
        if (v.match('gif') || v.match('png') || v.match('jpeg') || v.match('jpg'))
            return DisplayImage("/settings/files/" + v);
        else {
            let vArray = v.split(".");
            return <a href={"/settings/files/" + v} target="_blank" rel="noopener noreferrer">{vArray[vArray.length - 1]} file</a>;
        }
    }
    else if (props.processor == "RichTextEditor" && v != "") {
        // Strip HTML tags and limit to 100 characters
        v = v.replace(/<[^>]*>/g, '').trim();
        if (v.length > 100) {
            v = v.substr(0, 100) + '...';
        }
        return decodeHtmlEntities(v);
    }
    else if (props.processor == "AssociativeJSON" && v != "") {
        try {
            v = typeof v === 'string' ? JSON.parse(v) : v;
            let loopLimit = 3;
            if (v.length < 3)
                loopLimit = v.length;

            let output = '';

            for (let i = 0; i < loopLimit; i++) {
                if (typeof v[i].key != "undefined") {
                    // Parse HTML entities in both key and value
                    const parsedKey = typeof v[i].key === 'string' ? v[i].key : String(v[i].key);
                    const parsedValue = typeof v[i].value === 'string' ? v[i].value : String(v[i].value);
                    output = output + parsedKey + ': ' + parsedValue + ',\n';
                } else {
                    for (const property in v[i]) {
                        const parsedProperty = String(property);
                        const parsedValue = typeof v[i][property] === 'string' ? v[i][property] : String(v[i][property]);
                        output = output + parsedProperty + ': ' + parsedValue + ',\n';
                    }
                }
            }
            return decodeHtmlEntities(output + '...');
        } catch (error) {
            console.error('Error processing AssociativeJSON:', error);
            return String(v);
        }
    }
    else {
        if (typeof v === 'string' && v.length > 100)
            return decodeHtmlEntities(v.substr(0, 80) + '...');
        else return decodeHtmlEntities(String(v));
    }
}

function DisplayImage(logo) {
    return (
        <div style={{
            backgroundColor: '#f0f0f0', // Light grey background
            padding: '8px',
            borderRadius: '4px',
            display: 'inline-block',
            maxWidth: '200px'
        }}>
            <img
                src={logo}
                alt="Setting"
                style={{
                    maxWidth: '100%',
                    maxHeight: '80px',
                    display: 'block'
                }}
            />
        </div>
    )
}

// Using React.memo for performance optimization with Redux Form as per memory guidance
const GroupedSettings = React.memo(function GroupedSettings({
    settings,
    fetching,
    onClickRow,
    filters,
    onChangeFilter
}) {
    // Debug the incoming settings prop
    useEffect(() => {
        console.log('Settings prop received:', settings);
    }, [settings]);

    // Group settings by their group field with improved error handling
    const groupedSettings = useMemo(() => {
        const groups = {};
        if (Array.isArray(settings)) {
            settings.forEach(setting => {
                if (setting) {
                    // Normalize group name or use 'Uncategorized'
                    const group = (setting.group && typeof setting.group === 'string')
                        ? setting.group
                        : 'Uncategorized';

                    if (!groups[group]) {
                        groups[group] = [];
                    }
                    groups[group].push(setting);
                }
            });
        }
        console.log('Grouped settings:', groups);
        return groups;
    }, [settings]);

    // Define the hardcoded groups as specified
    const groupNames = [
        'Company Details',
        'Company Address',
        'Company T&C + Text',
        'Services',
        'RAMS',
        'Tool Box Talks',
        'Lists'
    ];

    // State to track the selected group - initialize with the current filter group if available
    const [selectedGroup, setSelectedGroup] = useState(
        filters && filters.group ? filters.group :
        groupNames.length > 0 ? groupNames[0] : null
    );

    // Sync the selected group with the Redux filter state when filters change
    useEffect(() => {
        if (filters && filters.group && filters.group !== selectedGroup) {
            setSelectedGroup(filters.group);
        }
    }, [filters, selectedGroup]);

    // Map display names to API values if needed
    const displayToApiMapping = {
        'Company Details': 'Company', // Map the display name back to the API value
        'Company T&C + Text': 'Company_text' // Map to Company_text as requested
        // Add other mappings if needed
    };

    // Handle group selection - using useCallback as per memory guidance
    // Debounced to reduce Redux Form updates (as per memory guidance)
    const handleGroupSelect = useCallback((groupName) => {
        console.log('Group selected:', groupName);
        setSelectedGroup(groupName);

        // Map display name to API value if needed
        const apiValue = displayToApiMapping[groupName] || groupName;
        console.log('Using API value:', apiValue);

        // Use the existing Redux filter mechanism
        onChangeFilter('group', apiValue);
    }, [onChangeFilter]);

    // Reverse mapping for display to API values
    const apiToDisplayMapping = {
        'Company': 'Company Details', // Map API value to display name
        'Company_text': 'Company T&C + Text' // Map API value to display name
        // Add other mappings if needed
    };

    // Get settings for the selected group
    // Use the groupedSettings object directly instead of filtering again
    const currentSettings = useMemo(() => {
        console.log('Selected group:', selectedGroup);
        console.log('Grouped settings object:', groupedSettings);

        if (!selectedGroup) {
            console.log('No selected group');
            return [];
        }

        // Check if this is a display name that maps to an API value
        const apiValue = displayToApiMapping[selectedGroup];

        if (apiValue) {
            // If we have a mapping, use the API value to find settings
            const groupSettings = groupedSettings[apiValue];
            if (groupSettings && Array.isArray(groupSettings)) {
                console.log('Found settings using API value mapping:', groupSettings);
                return groupSettings;
            }
        }

        // Try to get settings directly from the grouped data
        const groupSettings = groupedSettings[selectedGroup];

        if (groupSettings && Array.isArray(groupSettings)) {
            console.log('Found settings for group directly:', groupSettings);
            return groupSettings;
        }

        // If not found, try case-insensitive search
        const groupKeys = Object.keys(groupedSettings);
        for (const key of groupKeys) {
            if (key.toLowerCase() === selectedGroup.toLowerCase()) {
                console.log('Found settings with case-insensitive match:', groupedSettings[key]);
                return groupedSettings[key];
            }

            // Also check if this key maps to our selected display name
            const displayName = apiToDisplayMapping[key];
            if (displayName && displayName.toLowerCase() === selectedGroup.toLowerCase()) {
                console.log('Found settings via reverse mapping:', groupedSettings[key]);
                return groupedSettings[key];
            }
        }

        console.log('No settings found for group');
        return [];
    }, [selectedGroup, groupedSettings, displayToApiMapping]);

    return (
        <Layout className={styles.layout}>
            <Sider width={250} className={styles.sider} theme="light">
                <Menu
                    mode="inline"
                    selectedKeys={[selectedGroup]}
                    style={{ height: '100%' }}
                >
                    {groupNames.map(group => (
                        <Menu.Item
                            key={group}
                            onClick={() => handleGroupSelect(group)}
                        >
                            {group}
                        </Menu.Item>
                    ))}
                </Menu>
            </Sider>
            <Content className={styles.content}>
                <Card
                    title={
                        selectedGroup === 'Company T&C + Text' || selectedGroup === 'Company_text'
                            ? 'Company T&C and Text'
                            : (apiToDisplayMapping[selectedGroup] || selectedGroup)
                    }
                    className={styles.card}
                    loading={fetching}
                >
                    <List
                        itemLayout="horizontal"
                        dataSource={currentSettings}
                        header={
                            <div style={{ display: 'flex', fontWeight: 'bold', padding: '0 24px', marginBottom: '8px' }}>
                                <div style={{ width: '80px' }}>Type</div>
                                <div style={{ flex: 2 }}>Title</div>
                                <div style={{ flex: 3 }}>Value</div>
                            </div>
                        }
                        renderItem={item => (
                            <List.Item
                                key={item.id}
                                onClick={() => onClickRow(item.id)}
                                className={styles.listItem}
                                style={{ display: 'flex', alignItems: 'center', padding: '12px 24px' }}
                            >
                                {/* Type Column */}
                                <div style={{ width: '80px' }}>
                                    {item.type === 'user' && <Tag color='green' style={{ minWidth: '60px', textAlign: 'center' }}>User</Tag>}
                                    {item.type === 'team' && <Tag color='gold' style={{ minWidth: '60px', textAlign: 'center' }}>Team</Tag>}
                                    {item.type === 'system' && <Tag color='blue' style={{ minWidth: '60px', textAlign: 'center' }}>System</Tag>}
                                    {!item.type && <div style={{ minWidth: '60px', display: 'inline-block' }}></div>}
                                </div>

                                {/* Title Column */}
                                <div style={{ flex: 2 }}>
                                    <Text strong>{item.title}</Text>
                                    {item.help_text && (
                                        <Tooltip title={item.help_text}>
                                            <InfoCircleOutlined style={{ marginLeft: '8px', color: '#1890ff' }} />
                                        </Tooltip>
                                    )}
                                </div>

                                {/* Value Column */}
                                <div style={{ flex: 3 }}>
                                    <DisplayValue value={item.value} processor={item.processor} />
                                </div>
                            </List.Item>
                        )}
                    />
                </Card>
            </Content>
        </Layout>
    );
});

export default GroupedSettings;
